/**
 * Woodn Grail - facet list affordance + type-to-filter.
 *
 * The theme sets `scrollbar-width: none` on facet lists, so a 40+ item list
 * looks finished at item 9. CSS restores the scrollbar; this adds a per-facet
 * search box and a live option count read off the DOM.
 *
 * Three constraints from the theme it respects:
 *
 *  1. FacetFiltersForm.renderFilters() replaces innerHTML on every
 *     <collapsible-row> except the one clicked, so injected nodes are destroyed
 *     on first interaction. We re-apply via MutationObserver.
 *
 *  2. FacetFiltersForm listens for `input` on the <form> with a 500ms debounce
 *     and submits. Our input carries no `name` (so FormData never sees it and
 *     it can't reach the URL) and stops propagation (so it never wakes it).
 *
 *  3. Lists are wrapped in <scroll-shadow>, which measures its own children.
 *     We insert above that wrapper, never inside it.
 *
 * No dependencies. Idempotent. Safe on pages with no facets.
 */
(function () {
  'use strict';

  var MIN_ITEMS_FOR_SEARCH = 12; // below this a search box is noise
  var LIST_SELECTOR = '.facets .thb-filter-content ul:not(.list-color):not(.list--boxed)';
  var COMBINING = /[̀-ͯ]/g;
  var TRAILING_COUNT = /\(\s*\d[\d,]*\s*\)\s*$/;

  var queries = Object.create(null); // persist typed text across re-renders
  var applying = false;              // re-entrancy guard for the observer
  var scheduled = false;

  function norm(s) {
    return (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(COMBINING, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function countElements(node) {
    var n = 0;
    var child = node.firstElementChild;
    while (child) { n++; child = child.nextElementSibling; }
    return n;
  }

  // Separator-free form, so "hawaii" matches "Hawai'i" and "texasam" matches
  // "Texas A&M".
  function tight(spaced) {
    return spaced.replace(/ /g, '');
  }

  function labelText(li) {
    // Cached per <li>: avoids re-reading textContent on every keystroke.
    if (li._wgText === undefined) {
      var label = li.querySelector('label');
      var raw = ((label || li).textContent || '').replace(TRAILING_COUNT, '');
      li._wgText = norm(raw);
      li._wgTight = tight(li._wgText);
    }
    return li._wgText;
  }

  function keyFor(row, list) {
    var idx = (row && row.dataset && row.dataset.index) || '';
    if (!idx) {
      var holder = list.closest('[data-index]');
      idx = holder ? holder.dataset.index : 'f';
    }
    // Sidebar and mobile drawer share a data-index, so include the container.
    var scope = list.closest('#FacetFiltersFormMobile') ? 'm'
      : list.closest('#FacetFiltersForm-bar') ? 'b'
      : 'd';
    return scope + ':' + idx;
  }

  function applyQuery(list, wrap, empty, value) {
    var q = norm(value);
    var qt = tight(q);
    var shown = 0;
    var li = list.firstElementChild;
    var match;

    while (li) {
      // labelText() populates both cached forms on first call.
      match = !q || labelText(li).indexOf(q) !== -1 || li._wgTight.indexOf(qt) !== -1;
      // Only write when the state actually flips: no needless style recalc.
      if (match === li.hasAttribute('hidden')) {
        if (match) li.removeAttribute('hidden');
        else li.setAttribute('hidden', '');
      }
      if (match) shown++;
      li = li.nextElementSibling;
    }

    wrap.classList.toggle('is-filled', !!value);
    wrap.classList.toggle('is-empty', !!q && shown === 0);
    if (empty) {
      empty.textContent = (q && shown === 0) ? 'No matches for "' + value + '"' : '';
    }
  }

  function decorateCount(row, list, total) {
    var title = row.querySelector('.thb-filter-title');
    if (!title || !total) return;

    var badge = title.querySelector('.wg-facet-total');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'wg-facet-total';
      badge.setAttribute('aria-hidden', 'true');
      title.appendChild(badge);
    }
    var next = '(' + total + ')';
    if (badge.textContent !== next) badge.textContent = next;
  }

  function buildSearch(host, list, anchor, key) {
    var wrap = document.createElement('div');
    wrap.className = 'wg-facet-search';

    var input = document.createElement('input');
    input.type = 'search';
    input.className = 'wg-facet-search__input';
    input.autocomplete = 'off';
    input.placeholder = 'Search';
    input.setAttribute('aria-label', 'Search options');
    // Deliberately no `name`: FormData(form) must never see this field.

    var clear = document.createElement('button');
    clear.type = 'button'; // not submit - this lives inside the facet <form>
    clear.className = 'wg-facet-search__clear';
    clear.setAttribute('aria-label', 'Clear search');
    clear.innerHTML = '&times;';

    wrap.appendChild(input);
    wrap.appendChild(clear);

    var empty = document.createElement('p');
    empty.className = 'wg-facet-empty';
    empty.setAttribute('aria-live', 'polite');

    host.insertBefore(wrap, anchor);
    host.insertBefore(empty, anchor);

    function reset() {
      input.value = '';
      queries[key] = '';
      applyQuery(list, wrap, empty, '');
    }

    // stopPropagation is the load-bearing line: without it every keystroke
    // reaches FacetFiltersForm's debounced listener and refetches the grid.
    input.addEventListener('input', function (e) {
      e.stopPropagation();
      queries[key] = input.value;
      applyQuery(list, wrap, empty, input.value);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault(); // never submit the facet form
      if (e.key === 'Escape' && input.value) {
        e.stopPropagation();
        reset();
      }
    });

    clear.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      reset();
      input.focus();
    });

    return wrap;
  }

  function enhanceList(row, list) {
    var total = countElements(list);
    decorateCount(row, list, total);
    if (total < MIN_ITEMS_FOR_SEARCH) return;

    // The theme wraps facet lists in <scroll-shadow>, which measures its own
    // children to paint the fade. Insert above that wrapper, not inside it.
    var anchor = list;
    while (anchor.parentNode && anchor.parentNode.tagName === 'SCROLL-SHADOW') {
      anchor = anchor.parentNode;
    }
    var host = anchor.parentNode;
    if (!host) return;

    var key = keyFor(row, list);
    var wrap = host.querySelector(':scope > .wg-facet-search');
    if (!wrap) wrap = buildSearch(host, list, anchor, key);

    var empty = host.querySelector(':scope > .wg-facet-empty');
    var field = wrap.querySelector('.wg-facet-search__input');
    var saved = queries[key] || '';

    // Restore whatever was typed before the AJAX re-render replaced the list.
    if (field && field.value !== saved) field.value = saved;
    if (saved) applyQuery(list, wrap, empty, saved);
  }

  function run() {
    scheduled = false;
    var lists = document.querySelectorAll(LIST_SELECTOR);
    if (!lists.length) return;

    applying = true;
    try {
      for (var i = 0; i < lists.length; i++) {
        var list = lists[i];
        var row = list.closest('.thb-filter') || list.closest('collapsible-row');
        if (row) enhanceList(row, list);
      }
    } finally {
      // Release next frame so mutations we just caused are ignored.
      requestAnimationFrame(function () { applying = false; });
    }
  }

  function schedule() {
    if (scheduled || applying) return;
    scheduled = true;
    requestAnimationFrame(run);
  }

  function init() {
    // Scope the observer to the facet containers only. Watching document.body
    // would wake on every lazy image and cart drawer mount on the page.
    var roots = document.querySelectorAll(
      '#FacetFiltersForm, #FacetFiltersFormMobile, #FacetFiltersForm-bar, .facets'
    );
    if (!roots.length) return;

    run();

    var observer = new MutationObserver(function (records) {
      if (applying) return;
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) { schedule(); return; }
      }
    });

    for (var i = 0; i < roots.length; i++) {
      observer.observe(roots[i], { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();